"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import {
  CURRENCY_CHANGE_EVENT,
  getDefaultCurrencyForLanguage,
  loadDisplayCurrencyRates,
  normalizeCurrencyCode,
  resolveDisplayMoney,
} from "@/lib/currency-display"
import { cn } from "@/lib/util"
import { Button } from "@modules/components/ui/button"
import { requireLoginAndRun, useAuth } from "@/modules/auth"
import { useTranslation } from "@/i18n/client"
import { localizePathWithQuery } from "@/lib/account-links"
import { localizePathname } from "@/lib/locale-routing"
import { toAnalyticsErrorCode } from "@/lib/analytics/error-code"
import { trackYohoEvent } from "@/lib/analytics/track"
import type { DeduplicatedPlan } from "./types"
import {
  formatQuota,
  formatPrice,
  getUnitPrice,
  getOperatorsForCountries,
  computeTagBadges,
  getPlanScopeLabel,
  getPlanTypeLabel,
  getFlagUrl,
  derivePlanExhaust,
} from "./helpers"
import { ChevronRight, ListChecks } from "lucide-react"
import PlanDetailModal from "./PlanDetailModal"

const VISITOR_ID_STORAGE_KEY = "yoho-web-visitor-id"
const DIRECT_CHECKOUT_CART_ID_KEY = "yoho-direct-checkout-cart-id"
const DIRECT_CHECKOUT_CURRENCY_KEY = "yoho-direct-checkout-currency"
const CHECKOUT_RESUME_INTENT_KEY = "checkoutIntent"

type ThumbmarkWindow = Window & {
  ThumbmarkJS?: {
    getFingerprint?: () => Promise<string>
  }
  dataLayer?: Record<string, unknown>[]
}

function normalizeContinueHref(value: string) {
  const trimmed = value.trim()
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return trimmed
  }

  const segments = trimmed.split("/").filter(Boolean)
  if (segments.length === 0) {
    return "/"
  }

  const [maybeLocale, ...rest] = segments
  if (rest.length > 0 && /^[a-z]{2}(?:-[A-Z]{2})?$/.test(maybeLocale)) {
    return `/${rest.join("/")}`
  }

  return trimmed
}

function buildAccountLoginUrl(lng: string, redirectTo: string) {
  return localizePathWithQuery("/account/me", lng, {
    redirectTo: localizePathname(normalizeContinueHref(redirectTo), lng),
  })
}

function buildWebDeviceData() {
  return {
    version: 1,
    platform: "web",
    model: "Web Browser",
    screenWidth: String(window.innerWidth || window.screen?.width || 0),
    screenHeight: String(window.innerHeight || window.screen?.height || 0),
    identifierForVendor: window.navigator.userAgent,
    isPhysicalDevice: true,
    platformSpecificData: {
      userAgent: window.navigator.userAgent,
      language: window.navigator.language,
    },
  }
}

async function getOrCreateVisitorId() {
  const storedVisitorId = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY)
  if (storedVisitorId) {
    return storedVisitorId
  }

  const thumbmarkWindow = window as ThumbmarkWindow
  const getFingerprint = thumbmarkWindow.ThumbmarkJS?.getFingerprint

  if (typeof getFingerprint === "function") {
    try {
      const fingerprint = await getFingerprint()
      if (fingerprint) {
        window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, fingerprint)
        return fingerprint
      }
    } catch (error) {
      console.error("Failed to read Thumbmark fingerprint", error)
    }
  }

  const fallbackVisitorId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`

  window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, fallbackVisitorId)
  return fallbackVisitorId
}

function readPreferredCurrency() {
  if (typeof window === "undefined") {
    return "USD"
  }

  const savedCurrency = window.localStorage.getItem("selected-currency")
  if (savedCurrency) {
    return normalizeCurrencyCode(savedCurrency)
  }

  return getDefaultCurrencyForLanguage(
    document.documentElement.lang || window.navigator.language
  )
}

// =============================================================================
// i18n label maps
// =============================================================================

// Unified neutral palette: subtle stone for default, orange for brand emphasis.
// Avoid mixing brand colors (purple/blue/violet) which fight with the global
// orange identity.
const TAG_COLORS: Record<string, string> = {
  "Best Value": "bg-emerald-50 text-emerald-700",
  Popular: "bg-orange-50 text-orange-700",
  Hotspot: "bg-stone-100 text-stone-600",
  "Top-up Available": "bg-stone-100 text-stone-600",
  "Phone Number": "bg-stone-100 text-stone-600",
  "Yoho Care": "bg-stone-100 text-stone-600",
}

const SCOPE_COLORS: Record<string, string> = {
  "multi-country": "bg-stone-100 text-stone-600",
}

const TYPE_COLORS: Record<string, string> = {
  daily: "bg-stone-100 text-stone-600",
  unlimited: "bg-orange-100 text-orange-700",
}

const FOCUS_TAG_COLORS: Record<string, string> = {
  "precise-match": "bg-orange-100 text-orange-700",
  "multi-country": "bg-stone-100 text-stone-600",
  "peace-of-mind": "bg-orange-100 text-orange-700",
}

// =============================================================================
// Props
// =============================================================================

interface PlanCardProps {
  plan: DeduplicatedPlan
  iso2s: string[]
  countries: string
  variant: "recommended" | "grid"
  lng?: string
  primary?: boolean
  cardRole?: "default" | "reference" | "featured"
  ctaVariant?: "default" | "basic" | "multi-country"
  tagFocus?: "precise-match" | "multi-country" | "peace-of-mind"
  offerExplanation?: string
  savingsPercent?: number
  selected?: boolean
  onSelect?: () => void
  isUpgradeNudge?: boolean
  upgradePerDayCents?: number
  hideBanner?: boolean
  bannerTitle?: string
  /** When true, suppresses PlanCard's own outer border/ring/shadow so an outer wrapper can fully control the card silhouette. */
  noBorder?: boolean
  /** Maximum number of tag badges to render. Default keeps existing behavior (2 or 3 depending on focus). */
  maxTagBadges?: number
  /** When true, hides the inline tag badges row so an outer wrapper can render its own. */
  hideTags?: boolean
  /** When true, hides the "switch to this / selected" chip at the top of the card content area. */
  hideSelectionChip?: boolean
}

// =============================================================================
// Component
// =============================================================================

export default function PlanCard({
  plan,
  iso2s,
  countries,
  variant,
  lng,
  primary,
  cardRole = "default",
  ctaVariant = "default",
  tagFocus,
  offerExplanation,
  savingsPercent,
  selected,
  onSelect,
  isUpgradeNudge,
  upgradePerDayCents,
  hideBanner,
  bannerTitle,
  noBorder,
  maxTagBadges,
  hideTags,
  hideSelectionChip,
}: PlanCardProps) {
  const auth = useAuth()
  const { t } = useTranslation(lng, "product")
  const [showAlternatives, setShowAlternatives] = useState(false)
  const [showCountries, setShowCountries] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState("USD")
  const [currencyRates, setCurrencyRates] = useState<Record<string, number>>({})
  const [isStartingCheckout, setIsStartingCheckout] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  // 内联错误提示，替代 window.alert 阻塞弹窗。失败 4 秒后自动清除让用户重试。
  const [checkoutError, setCheckoutError] = useState("")
  const hasResumedCheckoutRef = useRef(false)
  const runCheckoutFlowRef = useRef<
    (mode: "protected" | "direct") => Promise<void>
  >(async () => {})

  useEffect(() => {
    const syncSelectedCurrency = (nextCurrency?: string) => {
      setSelectedCurrency(
        normalizeCurrencyCode(nextCurrency || readPreferredCurrency())
      )
    }

    const handleCurrencyChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ currency?: string }>
      syncSelectedCurrency(customEvent.detail?.currency)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "selected-currency") {
        syncSelectedCurrency(event.newValue || undefined)
      }
    }

    syncSelectedCurrency()
    window.addEventListener(
      CURRENCY_CHANGE_EVENT,
      handleCurrencyChange as EventListener
    )
    window.addEventListener("storage", handleStorage)

    return () => {
      window.removeEventListener(
        CURRENCY_CHANGE_EVENT,
        handleCurrencyChange as EventListener
      )
      window.removeEventListener("storage", handleStorage)
    }
  }, [])

  useEffect(() => {
    const resetCheckoutState = () => {
      setIsStartingCheckout(false)
    }

    window.addEventListener("pageshow", resetCheckoutState)

    return () => {
      window.removeEventListener("pageshow", resetCheckoutState)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    loadDisplayCurrencyRates()
      .then((rates) => {
        if (!cancelled) {
          setCurrencyRates(rates)
        }
      })
      .catch((error) => {
        console.warn("[PlanCard] Failed to load currency rates", error)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const operators = getOperatorsForCountries(plan.data.network, iso2s)

  const { amount: displayAmount, currencySymbol } = resolveDisplayMoney({
    amount: plan.amount,
    sgAmount: plan.sg_amount,
    selectedCurrency,
    rates: currencyRates,
  })

  const unitPrice = getUnitPrice(
    displayAmount,
    plan.data.quota,
    plan.data.daily
  )
  const quotaLabel = formatQuota(plan.data.quota, t("plan-quota-unlimited"))
  const displayQuotaLabel =
    plan.data.daily && plan.data.quota !== -1
      ? t("plan-card-daily-data", { data: quotaLabel })
      : quotaLabel
  const tagBadges = computeTagBadges(plan).filter((tag) => {
    if (tagFocus && tag === "Best Value") return false
    if (
      (tagFocus === "precise-match" || tagFocus === "multi-country") &&
      tag === "Popular"
    ) {
      return false
    }
    return true
  })
  const scopeLabel = getPlanScopeLabel(plan, lng)
  const typeLabel = getPlanTypeLabel(plan, lng)
  const planExhaust = derivePlanExhaust(plan)
  const focusTagLabel = tagFocus ? t(`plan-card-focus-${tagFocus}`) : null

  const quotaParam =
    plan.data.quota === -1 ? "unlimited" : String(plan.data.quota)
  const checkoutIntentId = [
    plan.provider,
    plan.data.variant_id || plan.data.product_id || "plan",
    plan.data.day,
    quotaParam,
  ].join(":")
  const isRecommended = variant === "recommended"
  const hasAlternatives = plan.alternativeProviders.length > 0

  const isSelected = selected ?? primary
  const isUpgrade = !!isUpgradeNudge && upgradePerDayCents !== undefined
  const upgradePerDayMoney = isUpgrade
    ? resolveDisplayMoney({
        amount: Math.abs(upgradePerDayCents || 0),
        selectedCurrency,
        rates: currencyRates,
      })
    : null
  const upgradePerDayStr =
    isUpgrade && upgradePerDayCents! > 0 && upgradePerDayMoney
      ? `${upgradePerDayMoney.currencySymbol}${formatPrice(upgradePerDayMoney.amount)}`
      : ""
  const upgradeIsCheaper = isUpgrade && upgradePerDayCents! <= 0
  const isReferenceCard = cardRole === "reference"
  const isFeaturedCard = cardRole === "featured"
  const isBasicCard = ctaVariant === "basic"
  const isComparisonCard = ctaVariant === "multi-country"
  const isAlternativePick =
    isRecommended &&
    !primary &&
    !isUpgrade &&
    !!offerExplanation &&
    !(savingsPercent && savingsPercent > 0)
  const isMultiCountry = (plan.data.support_countries?.length ?? 0) > 1
  const hasBanner =
    !hideBanner &&
    (primary ||
      isUpgrade ||
      !!(savingsPercent && savingsPercent > 0) ||
      (isRecommended && !!offerExplanation))
  const ctaLabel =
    ctaVariant === "basic"
      ? t("plan-card-start-basic")
      : ctaVariant === "multi-country"
        ? t("plan-card-pick-multi-country")
        : primary
          ? t("plan-card-get-plan")
          : isUpgrade
            ? upgradeIsCheaper
              ? t("plan-card-go-unlimited-lower")
              : t("plan-card-go-unlimited-cost", { cost: upgradePerDayStr })
            : savingsPercent && savingsPercent > 0
              ? `${t("plan-card-savings", { percent: savingsPercent })} — ${t("plan-card-grab-deal")}`
              : t("plan-card-get-plan")

  const buildResumeCheckoutHref = () => {
    const url = new URL(window.location.href)
    url.searchParams.set(CHECKOUT_RESUME_INTENT_KEY, checkoutIntentId)
    return `${url.pathname}${url.search}${url.hash}`
  }

  const clearResumeCheckoutIntent = () => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(CHECKOUT_RESUME_INTENT_KEY)) {
      return
    }

    url.searchParams.delete(CHECKOUT_RESUME_INTENT_KEY)
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    )
  }

  const openDirectCheckout = async () => {
    const visitorId = await getOrCreateVisitorId()
    const planId = plan.data.variant_id || plan.data.product_id
    const checkoutCurrency =
      window.localStorage.getItem("selected-currency")?.trim().toLowerCase() ||
      selectedCurrency.toLowerCase()

    // The plan's actual country set — for single-country plans this matches
    // the page's `countries` prop, but smart-recommendations also surfaces
    // multi-country bundles (e.g. SG_YE @ $8.69) on a single-country page
    // where the page prop is "YE". Sending the page prop to the backend
    // would make resolveVariantId look up the wrong (Yemen-only) variant,
    // its price wouldn't match plan.amount, and validate_variant_price would
    // 500 the checkout. support_countries carries the bundle's real country
    // combo and must win when it differs from the page.
    const planCountries =
      Array.isArray(plan.data.support_countries) &&
      plan.data.support_countries.length > 0
        ? plan.data.support_countries.join("_")
        : countries
    const planQuota =
      typeof plan.data.quota === "number" && plan.data.quota === -1
        ? "unlimited"
        : typeof plan.data.quota === "number"
          ? String(plan.data.quota)
          : quotaParam

    trackYohoEvent("plan_buy_click", {
      plan_id: planId,
      country_code: planCountries,
      price: plan.amount,
      currency: checkoutCurrency.toUpperCase(),
      provider: plan.provider,
      quota: plan.data.quota,
      day: plan.data.day,
      source: "plan-card",
    })

    let result: any = null
    try {
      const response = await fetch("/api/direct-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: plan.amount,
          countries: planCountries,
          day: plan.data.day,
          deviceData: buildWebDeviceData(),
          locale: lng || "en",
          provider: plan.provider,
          quota: planQuota,
          selectedCurrency: checkoutCurrency,
          variantId: plan.data.variant_id,
          visitorId,
        }),
      })

      result = await response.json().catch(() => null)

      if (!response.ok || !result?.success || !result?.checkoutUrl) {
        throw new Error(result?.message || "direct_checkout_failed")
      }
    } catch (error) {
      const errorCode = toAnalyticsErrorCode(error, "direct_checkout_failed")
      trackYohoEvent("direct_checkout_create_failed", {
        plan_id: planId,
        country_code: countries,
        currency: checkoutCurrency.toUpperCase(),
        provider: plan.provider,
        error_code: errorCode,
        error_message_code: errorCode,
      })
      throw error
    }

    const cartCurrencyCode =
      typeof result?.cartCurrencyCode === "string" &&
      result.cartCurrencyCode.trim()
        ? result.cartCurrencyCode.trim().toUpperCase()
        : checkoutCurrency.toUpperCase()

    window.sessionStorage.setItem(DIRECT_CHECKOUT_CART_ID_KEY, result.cartId)
    window.sessionStorage.setItem(
      DIRECT_CHECKOUT_CURRENCY_KEY,
      cartCurrencyCode
    )
    window.localStorage.setItem("selected-currency", cartCurrencyCode)

    trackYohoEvent("direct_checkout_create_success", {
      checkout_id_hash: result.cartId,
      plan_id: planId,
      country_code: countries,
      currency: cartCurrencyCode,
      provider: plan.provider,
    })

    const w = window as ThumbmarkWindow
    w.dataLayer?.push({
      event: "click_plan_buy",
      locale: lng || "en",
      country: countries,
      planId,
      provider: plan.provider,
      amount: plan.amount,
      cartCurrencyCode,
      quota: plan.data.quota,
      day: plan.data.day,
      targetUrl: result.checkoutUrl,
      sourceSection: "plan-card",
      recommendationType: primary
        ? "exact"
        : isUpgrade
          ? "upgrade_nudge"
          : savingsPercent
            ? "inventory"
            : "recommended",
    })

    window.location.assign(result.checkoutUrl)
  }

  const runCheckoutFlow = async (mode: "protected" | "direct") => {
    if (isStartingCheckout) {
      return
    }

    setIsStartingCheckout(true)

    try {
      if (mode === "protected") {
        await requireLoginAndRun(openDirectCheckout, {
          loginUrl: buildAccountLoginUrl(
            lng || "en",
            buildResumeCheckoutHref()
          ),
        })
      } else {
        await openDirectCheckout()
      }
      setIsStartingCheckout(false)
    } catch (error) {
      console.error("Direct checkout failed", error)
      setCheckoutError(t("plan-card-checkout-error"))
      setIsStartingCheckout(false)
      // 4 秒后清错误，避免一直挂着旧报错让用户困惑
      window.setTimeout(() => setCheckoutError(""), 4000)
    }
  }

  runCheckoutFlowRef.current = runCheckoutFlow

  useEffect(() => {
    if (auth.status !== "authenticated" || hasResumedCheckoutRef.current) {
      return
    }

    const resumeIntent = new URL(window.location.href).searchParams.get(
      CHECKOUT_RESUME_INTENT_KEY
    )
    if (resumeIntent !== checkoutIntentId) {
      return
    }

    hasResumedCheckoutRef.current = true
    clearResumeCheckoutIntent()
    void runCheckoutFlowRef.current("direct")
  }, [auth.status, checkoutIntentId])

  const handleCheckoutClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
    event.stopPropagation()

    try {
      await runCheckoutFlow("protected")
    } catch (error) {
      console.error("Checkout guard failed", error)
    }
  }

  if (!plan.amount || plan.amount <= 0 || !isFinite(plan.amount)) return null
  const formattedPrice = formatPrice(displayAmount)
  if (formattedPrice === "—") return null

  // Unified orange selected state — every variant uses the same brand ring.
  // Featured (unlimited upgrade) gets a slightly stronger lift to emphasize the
  // "top pick" status; everything else shares the same ring/shadow.
  const selectedCardClass = isFeaturedCard
    ? "border-home-orange_1 ring-2 ring-orange-300 ring-offset-2 ring-offset-white bg-gradient-to-b from-orange-50/95 via-white to-orange-50/75 shadow-[0_34px_78px_-32px_rgba(234,88,12,0.55)] md:-translate-y-3"
    : "border-home-orange_1 ring-2 ring-orange-200 ring-offset-2 ring-offset-white bg-orange-50/50 shadow-[0_20px_42px_-28px_rgba(249,115,22,0.42)]"

  const idleCardClass = isFeaturedCard
    ? "border-orange-300 bg-gradient-to-b from-orange-50 via-white to-orange-50/70 shadow-[0_22px_52px_-32px_rgba(234,88,12,0.45)] hover:border-orange-400 hover:shadow-[0_28px_60px_-34px_rgba(234,88,12,0.55)] md:-translate-y-2"
    : isBasicCard || isReferenceCard
      ? "border-orange-200 bg-orange-50/25 shadow-[0_14px_30px_-30px_rgba(249,115,22,0.35)] hover:border-orange-300 hover:bg-orange-50/40"
      : isComparisonCard
        ? "border-stone-200 bg-white shadow-sm hover:border-orange-300 hover:bg-orange-50/15"
        : isUpgrade
          ? "border-orange-300 bg-orange-50/20 shadow-sm hover:border-orange-400 hover:shadow-md"
          : savingsPercent && savingsPercent > 0
            ? "border-orange-200 bg-orange-50/15 shadow-[0_12px_24px_-28px_rgba(249,115,22,0.25)] hover:border-orange-300 hover:bg-orange-50/25"
            : isAlternativePick
              ? "border-stone-200 bg-white shadow-sm hover:border-orange-300 hover:shadow-md"
              : isRecommended
                ? "border-stone-200 bg-white shadow-sm hover:border-home-orange_1/40"
                : "border-stone-200 bg-white hover:border-home-orange_1/40 hover:shadow-sm"

  // Price color stratification: unlimited / upgrade plans use brand orange to
  // signal "premium upgrade"; finite plans stay neutral dark so the saving
  // doesn't get visually outshouted by the orange price tag everywhere.
  const priceAccentClass = isUpgrade ? "text-home-orange_1" : "text-stone-900"

  const isSelectableChoice = isRecommended && !!onSelect

  // Unified solid-orange CTA on every card — selection state is already
  // signaled by the card border + banner so the buy action stays equally
  // discoverable on muted cards too.
  const ctaClass =
    "border border-home-orange_1 bg-home-orange_1 text-white shadow-[0_16px_34px_-22px_rgba(249,115,22,0.55)] hover:bg-orange-700 hover:brightness-105"

  return (
    <div
      onClick={onSelect}
      className={cn(
        "relative flex flex-1 flex-col transition-all duration-300",
        !noBorder && "overflow-hidden rounded-2xl border",
        onSelect && "cursor-pointer",
        !noBorder && (isSelected ? selectedCardClass : idleCardClass),
        !noBorder && isSelectableChoice && isSelected && "z-10 md:scale-[1.01]",
        !noBorder &&
          isSelectableChoice &&
          !isSelected &&
          "opacity-75 saturate-[0.82] hover:opacity-100 hover:saturate-100 md:scale-[0.985] md:hover:scale-100"
      )}
    >
      {isFeaturedCard && !noBorder && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-6 -top-6 h-24 rounded-full bg-orange-300/25 blur-2xl"
        />
      )}
      {/* ── Top Banner — unified orange variants ── */}
      {!hideBanner &&
        (isFeaturedCard ? (
          // Featured (unlimited upgrade): solid orange gradient + 推荐 chip
          <div className="rounded-t-2xl bg-gradient-to-r from-[#fb923c] to-home-orange_1 px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="m17 14-5-5-5 5" />
                <path d="M12 19V9" />
              </svg>
              <span>{bannerTitle || t("recommendation-banner-unlimited")}</span>
              <span className="bg-white/22 ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-[0.01em]">
                {t("recommendation-banner-top-pick")}
              </span>
            </div>
            {offerExplanation && (
              <p className="mt-1 text-[10px] leading-snug text-white/85">
                {offerExplanation}
              </p>
            )}
          </div>
        ) : primary ? (
          // Primary recommendation: solid orange
          <div className="rounded-t-2xl bg-home-orange_1 px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              <span>{bannerTitle || t("recommendation-banner-top-pick")}</span>
            </div>
            {offerExplanation && (
              <p className="mt-1 text-[10px] leading-snug text-white/80">
                {offerExplanation}
              </p>
            )}
          </div>
        ) : savingsPercent && savingsPercent > 0 ? (
          // Better deal: light orange tinted with savings chip
          <div className="rounded-t-2xl border-b border-orange-200 bg-orange-100/85 px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-home-orange_1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              <span>
                {bannerTitle || t("recommendation-banner-better-deal")}
              </span>
              <span className="ml-auto shrink-0 rounded-full bg-home-orange_1 px-2 py-0.5 text-[11px] font-bold text-white">
                {t("plan-card-savings", { percent: savingsPercent })}
              </span>
            </div>
            {offerExplanation && (
              <p className="mt-1 text-[10px] leading-snug text-home-orange_1/75">
                {offerExplanation}
              </p>
            )}
          </div>
        ) : isReferenceCard ||
          isComparisonCard ||
          (isRecommended && offerExplanation) ? (
          // Soft orange tinted banner for: reference, multi-country, alternative recommended
          <div className="rounded-t-2xl border-b border-orange-200 bg-orange-100/85 px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-home-orange_1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                {isComparisonCard ? (
                  <>
                    <path d="M2 12h20" />
                    <path d="M12 2a14.5 14.5 0 0 0 0 20" />
                    <path d="M12 2a14.5 14.5 0 0 1 0 20" />
                  </>
                ) : (
                  <>
                    <circle cx="12" cy="12" r="10" />
                    <path d="m9 12 2 2 4-4" />
                  </>
                )}
              </svg>
              <span>
                {bannerTitle ||
                  (isComparisonCard
                    ? t("recommendation-banner-multi-country")
                    : isReferenceCard
                      ? t("recommendation-banner-top-pick")
                      : t("plan-card-also-recommended"))}
              </span>
            </div>
            {offerExplanation && (
              <p className="mt-1 text-[10px] leading-snug text-home-orange_1/75">
                {offerExplanation}
              </p>
            )}
          </div>
        ) : null)}

      <div
        className={cn(
          "flex flex-1 flex-col px-4 pb-3",
          hasBanner ? "pt-3" : "pt-4"
        )}
      >
        {isSelectableChoice && !hideSelectionChip && (
          <div className="mb-3 flex items-center">
            <div
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors",
                isSelected
                  ? "border-home-orange_1/20 bg-orange-50 text-home-orange_1 shadow-sm"
                  : "border-gray-200 bg-white text-gray-500"
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "h-2.5 w-2.5 rounded-full",
                  isSelected
                    ? "bg-home-orange_1 shadow-[0_0_0_3px_rgba(249,115,22,0.18)]"
                    : "border border-gray-300 bg-white"
                )}
              />
              <span>
                {t(
                  isSelected ? "plan-card-selected" : "plan-card-switch-to-this"
                )}
              </span>
            </div>
          </div>
        )}
        <div className="mb-3">
          <div className="text-2xl font-bold leading-none text-gray-900">
            {displayQuotaLabel}
          </div>
          <div className="mt-1 text-sm text-gray-400">
            {plan.data.daily
              ? t("plan-card-daily-label")
              : t("plan-card-total-label")}
            {" · "}
            {plan.data.day === 1
              ? t("plan-card-days-count-singular", { count: plan.data.day })
              : t("plan-card-days-count", { count: plan.data.day })}
          </div>
        </div>

        <div className="mb-3">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                "text-3xl font-bold tabular-nums leading-none",
                priceAccentClass
              )}
            >
              {currencySymbol}
              {formattedPrice}
            </span>
          </div>
          {plan.data.day > 0 && (
            <p className="mt-1.5 text-[11px] tabular-nums text-stone-400">
              {currencySymbol}
              {(displayAmount / 100 / plan.data.day).toFixed(2)}/
              {t("plan-card-day-unit")}
              {unitPrice && ` · ${currencySymbol}${unitPrice}/GB`}
            </p>
          )}
        </div>

        <div className="mb-3 border-t border-gray-100" />

        {!hideTags &&
          (focusTagLabel ||
            scopeLabel ||
            typeLabel ||
            tagBadges.length > 0) && (
            <div className="mb-2.5 flex flex-wrap gap-1.5">
              {focusTagLabel && (
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                    FOCUS_TAG_COLORS[tagFocus!] || "bg-gray-100 text-gray-700"
                  )}
                >
                  {focusTagLabel}
                </span>
              )}
              {scopeLabel && (
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] font-medium",
                    SCOPE_COLORS[scopeLabel] || "bg-gray-100 text-gray-600"
                  )}
                >
                  {t(`plan-scope-${scopeLabel}`)}
                </span>
              )}
              {typeLabel && (
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] font-medium",
                    TYPE_COLORS[typeLabel] || "bg-gray-100 text-gray-600"
                  )}
                >
                  {t(`plan-type-${typeLabel}`)}
                </span>
              )}
              {tagBadges
                .slice(0, maxTagBadges ?? (focusTagLabel ? 3 : 4))
                .map((tag) => (
                  <span
                    key={tag}
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-[11px] font-medium",
                      TAG_COLORS[tag] || "bg-gray-100 text-gray-600"
                    )}
                  >
                    {t(
                      `plan-tag-${tag.toLowerCase().replace(/ /g, "-").replace(/\+/g, "")}`
                    )}
                  </span>
                ))}
            </div>
          )}

        {/* Noname (T-Mobile US) plans need the user's EID/IMEI2 + a compatible
            phone — surface this BEFORE the buy click so the user can bail out
            instead of paying for a plan they can't use. The actual form lives
            on the checkout page (NonameExtraInfo); this is the heads-up. */}
        {plan.provider === "N" && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg bg-orange-50 px-2.5 py-1.5 text-[11px] leading-snug text-orange-800">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
              aria-hidden
            >
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
            <span className="font-medium">
              {t("plan-noname-device-required-prefix")}
            </span>
            <Link
              href={`/${lng || "en"}/esim-compatible-list`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="cursor-pointer underline decoration-orange-300 underline-offset-2 hover:text-orange-900"
            >
              {t("plan-noname-device-required-link")}
            </Link>
          </div>
        )}

        {/* CMLink (HK/TW) eSIM plans need carrier-side real-name registration
            before they can be activated. Surface a heads-up on the card so
            the user knows to step through /account/verifications/cmlink at
            some point. Limited to plans that actually cover HK or TW so we
            don't fire it on any future non-HK/TW CMLink product. */}
        {plan.provider === "C" &&
          Array.isArray(plan.data.support_countries) &&
          plan.data.support_countries.some(
            (code) => code === "HK" || code === "TW"
          ) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg bg-orange-50 px-2.5 py-1.5 text-[11px] leading-snug text-orange-800">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden
              >
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
              <span className="font-medium">
                {t("plan-cmlink-realname-prefix")}
              </span>
              <Link
                href={`/${lng || "en"}/account/verifications/cmlink`}
                onClick={(event) => event.stopPropagation()}
                className="cursor-pointer underline decoration-orange-300 underline-offset-2 hover:text-orange-900"
              >
                {t("plan-cmlink-realname-link")}
              </Link>
            </div>
          )}

        {/* Per-country networks — inline, transparent, expandable */}
        {plan.data.support_countries &&
          plan.data.support_countries.length > 0 &&
          (() => {
            const networkByCountry = iso2s
              .map((iso2) => {
                const code = iso2.toUpperCase()
                const ops = (
                  plan.data.network[code] ||
                  plan.data.network[iso2] ||
                  []
                ).map((op) => ({
                  operator: op.operator,
                  has5G: op.speed.some((s) => s.includes("5G")),
                }))
                return {
                  iso2: code,
                  operators: ops,
                  has5G: ops.some((o) => o.has5G),
                }
              })
              .filter((c) => c.operators.length > 0)

            if (networkByCountry.length === 0) return null

            const totalNetworks = networkByCountry.reduce(
              (acc, c) => acc + c.operators.length,
              0
            )
            const VISIBLE = 5
            const isExpandable = networkByCountry.length > VISIBLE
            const visible = showCountries
              ? networkByCountry
              : networkByCountry.slice(0, VISIBLE)
            const hiddenCount = networkByCountry.length - VISIBLE

            const renderRow = (c: (typeof networkByCountry)[number]) => (
              <li key={c.iso2} className="flex items-center gap-2">
                <Image
                  src={getFlagUrl(c.iso2)}
                  alt=""
                  width={16}
                  height={12}
                  className="shrink-0 rounded-[1px] object-cover"
                />
                <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-stone-600">
                  {c.operators.map((o) => o.operator).join(" · ")}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {c.has5G && (
                    <span className="rounded bg-emerald-50 px-1 text-[9px] font-bold leading-relaxed text-emerald-700">
                      5G
                    </span>
                  )}
                  <span className="rounded bg-sky-50 px-1 text-[9px] font-bold leading-relaxed text-sky-700">
                    4G
                  </span>
                </span>
              </li>
            )

            return (
              <div className="mb-3">
                <div className="mb-1.5 flex items-baseline justify-between gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                    {networkByCountry.length === 1
                      ? t("plan-card-network-header-single")
                      : t("plan-card-network-header-multi", {
                          countries: networkByCountry.length,
                          networks: totalNetworks,
                        })}
                  </span>
                </div>
                <ul className="space-y-1.5">{visible.map(renderRow)}</ul>
                {isExpandable && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowCountries((v) => !v)
                    }}
                    className="mt-1.5 text-[11px] font-medium text-home-orange_1 hover:underline"
                  >
                    {showCountries
                      ? t("plan-card-collapse-countries")
                      : t("plan-card-expand-rest", {
                          count: hiddenCount,
                        })}
                  </button>
                )}
              </div>
            )
          })()}

        {hasAlternatives && (
          <div className="mb-2.5">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowAlternatives(!showAlternatives)
              }}
              className="text-xs text-home-orange_1 hover:underline"
            >
              {showAlternatives
                ? t("plan-card-hide-options")
                : plan.alternativeProviders.length > 1
                  ? t("plan-card-more-options-plural", {
                      count: plan.alternativeProviders.length,
                    })
                  : t("plan-card-more-options", {
                      count: plan.alternativeProviders.length,
                    })}
            </button>
            {showAlternatives && (
              <div className="mt-2 space-y-1.5">
                {plan.alternativeProviders.map((alt, i) => {
                  const altOps = getOperatorsForCountries(alt.network, iso2s)
                  const altDisplayMoney = resolveDisplayMoney({
                    amount: alt.amount,
                    selectedCurrency,
                    rates: currencyRates,
                  })
                  const altPrice = formatPrice(altDisplayMoney.amount)
                  if (altPrice === "—") return null
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs"
                    >
                      <span className="text-gray-600">
                        {altOps
                          .slice(0, 2)
                          .map((o) => o.operator)
                          .join(", ")}
                      </span>
                      <span className="font-medium text-gray-700">
                        {altDisplayMoney.currencySymbol}
                        {altPrice}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Spacer ── */}
        <div className="flex-1" />

        {/* ── Plan details entry → opens centered detail modal ── */}
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={(e) => {
            e.stopPropagation()
            setDetailOpen(true)
          }}
          className="mb-2.5 flex w-full cursor-pointer items-center gap-2 rounded-xl border border-stone-200 bg-stone-50/60 px-3 py-2.5 text-left transition-colors hover:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/50"
        >
          <ListChecks className="h-4 w-4 shrink-0 text-stone-400" aria-hidden />
          <span className="flex-1 text-[13px] font-semibold text-stone-700">
            {t("plan-detail-entry")}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" aria-hidden />
        </button>
        <PlanDetailModal
          open={detailOpen}
          onOpenChange={setDetailOpen}
          plan={plan}
          iso2s={iso2s}
          exhaust={planExhaust}
          priceLabel={`${currencySymbol}${formattedPrice}`}
          buying={isStartingCheckout}
          onBuy={() => {
            void runCheckoutFlow("protected")
          }}
          lng={lng}
        />

        {/* ── 9. CTA ── */}
        <Button
          type="button"
          disabled={isStartingCheckout}
          onClick={handleCheckoutClick}
          aria-busy={isStartingCheckout}
          className={cn(
            "h-auto min-h-10 w-full whitespace-normal text-balance break-words rounded-3xl px-3 py-2 text-sm font-semibold leading-tight shadow-sm transition-colors disabled:cursor-wait disabled:opacity-80",
            ctaClass
          )}
        >
          {isStartingCheckout ? t("filter-loading") : ctaLabel}
        </Button>
        {checkoutError ? (
          <p
            role="alert"
            className="mt-2 text-center text-[12px] leading-5 text-rose-600"
          >
            {checkoutError}
          </p>
        ) : null}
      </div>
    </div>
  )
}
